/**
 * `/stories` — the shelf, and the hub the rest of the peripheral surfaces hangs off.
 *
 * Every peripheral surface is one click from here: creation, the leaderboard, settings,
 * feedback, sign-out. That is deliberate and it is the reason `/` is a redirect rather than a
 * page — a returning player should land in their story, not in a menu, and a player who wants
 * the menu gets to it from the story in one click (`PlayRoute`'s "stories" control).
 *
 * P7's budget is rendered *before* the create control and decides whether it is offered. That
 * is the whole point of putting `stories_used` and `max_stories_allowed` on the profile: the
 * client can say "you have no slots left, delete a finished run" instead of letting a player
 * fill in a character sheet and answering it with a 403.
 */
import { Link } from 'react-router';
import { describeApiError } from '@/api/errorMessages';
import { usePlayGate, useProfile, useStories, useStoryBudget } from '@/api/queries';
import { useAuth } from '@/auth/context';
import { StoryShelf } from '@/features/stories/StoryShelf';
import { useT } from '@/i18n/context';
import { Button } from '@/ui/Button';
import { Page } from '@/ui/Page';

export function StoriesRoute() {
  const t = useT();
  const { token, signOut } = useAuth();
  const stories = useStories();
  const profile = useProfile();
  const budget = useStoryBudget();
  const gate = usePlayGate();

  return (
    <Page title={t('ui.app.title')}>
      {stories.isPending ? (
        <p className="text-fg-muted" style={{ fontSize: 'var(--text-action)' }}>
          {t('ui.story.loading')}
        </p>
      ) : stories.error ? (
        <p className="text-fg-muted" style={{ fontSize: 'var(--text-action)' }}>
          {describeApiError(t, stories.error)}
        </p>
      ) : (
        <StoryShelf stories={stories.data} />
      )}

      {/*
        One of the two missing empty states: the profile failing is the one state this page had no
        words for. `budget` is null
        both while it loads and when it 500s, and the create control hangs off it — so a
        failed `GET /me` left a player looking at a shelf with no way to start a story and no
        reason given. The message is the same envelope every other failure uses.
      */}
      {profile.error ? (
        <p className="text-fg-muted" style={{ fontSize: 'var(--text-action)' }}>
          {describeApiError(t, profile.error)}
        </p>
      ) : budget ? (
        <div className="flex flex-col gap-3">
          <p className="text-fg-faint font-mono" style={{ fontSize: 'var(--text-meta)' }}>
            {budget.canCreate
              ? t('ui.story.slots_free', { remaining: budget.remaining, max: budget.max })
              : gate?.canPlay === false
                ? /* Not a slot problem. Saying "you have used all 10 stories" to a player
                   * with nine free ones is worse than saying nothing, and the gate's own
                   * two sentences are the ones that tell them what to do about it. */
                  t(gate.reason ?? 'ui.error.forbidden')
                : t('ui.story.slots_full', { max: budget.max })}
          </p>
          {budget.canCreate ? (
            <Link
              to="/stories/new"
              className="border-accent text-accent hover:bg-accent hover:text-bg w-fit border px-4 py-2 font-mono transition-colors"
              style={{ fontSize: 'var(--text-action)' }}
            >
              {t('ui.story.new')}
            </Link>
          ) : null}
        </div>
      ) : null}

      <nav
        className="border-rule text-fg-faint mt-auto flex flex-wrap items-center gap-x-5 gap-y-2 border-t pt-6 font-mono"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        <Link to="/leaderboard" className="hover:text-accent transition-colors">
          {t('ui.nav.leaderboard')}
        </Link>
        <Link to="/settings" className="hover:text-accent transition-colors">
          {t('ui.nav.settings')}
        </Link>
        {/* No feedback link here any more: `App` mounts a global one, and two identical
            controls on one screen is worse than the gap this used to cover. */}
        <Button variant="bare" className="ml-auto px-0" onClick={signOut}>
          {t('ui.auth.sign_out')} {profile.data?.email ?? token?.email}
        </Button>
      </nav>
    </Page>
  );
}
